// ✅ SKILL FACTURXPRO: Architecture 3 couches
// Component (UI only) → Custom Hook (Logic) → API Layer (HTTP)
// ✅ MIGRATED TO V2 DESIGN SYSTEM - Sprint 3

import { useTranslation } from 'react-i18next';
import { useCreditNotesManagement } from "@/hooks/useCreditNotesManagement";
import { useInvoicesManagement } from "@/hooks/useInvoicesManagement";
import { confirm } from "@/stores/confirmStore";
import { CreditNoteStatus } from "@/utils/creditNotesAPI";
import {
  Calendar,
  CheckCircle,
  Download,
  Edit,
  EuroIcon,
  Eye,
  FileEdit,
  FileText,
  MoreVertical,
  Plus,
  Send,
  Trash2,
} from "lucide-react";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

// ✅ V2 DESIGN SYSTEM IMPORTS
import {
  Badge,
  Button,
  DropdownMenu,
  EmptyState,
  MobileCard,
  Select,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  type DropdownMenuItem,
} from "@/components/ui-v2";

import {
  FilterBar,
  FilterOption,
  SearchInput,
} from '@/components/ui';
import { downloadCSV, formatDateForExport, formatCurrencyForExport, type ExportColumn } from '@/utils/exportUtils';

const CreditNotesManagement: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // ✅ SKILL FACTURXPRO: Logique extraite vers custom hook
  const {
    // Data
    creditNotes,
    pagination,
    loading,
    // Filter State
    searchTerm,
    statusFilter,
    // Actions
    deleteCreditNote,
    updateStatus,
    applyCreditNote,
    // UI Actions
    setSearchTerm,
    setStatusFilter,
    goToPage,
    formatCurrency,
    formatDate,
    getStatusLabel,
  } = useCreditNotesManagement();

  // ✅ Charger les factures pour la sélection
  const {
    invoices,
    loading: loadingInvoices,
    formatCurrency: formatCurrencyInvoice,
    formatDate: formatDateInvoice,
  } = useInvoicesManagement();

  // Local state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [invoiceSearchTerm, setInvoiceSearchTerm] = useState('');

  // ✅ SKILL FACTURXPRO: Handlers simplifiés (UI only - confirmation dialogs)
  const handleDelete = async (id: string, number: string) => {
    const confirmed = await confirm({
      title: t('billing.creditNotes.deleteTitle'),
      message: t('billing.creditNotes.deleteMessage', { number }),
      confirmText: t('common.actions.delete'),
      cancelText: t('common.actions.cancel'),
      variant: 'danger',
    });
    if (!confirmed) return;

    await deleteCreditNote(id);
  };

  const handleStatusChange = async (id: string, currentStatus: CreditNoteStatus) => {
    if (currentStatus === 'DRAFT') {
      const confirmed = await confirm({
        title: t('billing.creditNotes.sendTitle'),
        message: t('billing.creditNotes.sendMessage'),
        confirmText: t('common.actions.send'),
        cancelText: t('common.actions.cancel'),
        variant: 'info',
      });
      if (!confirmed) return;

      await updateStatus(id, 'SENT');
    }
  };

  const handleApply = async (id: string, number: string) => {
    const confirmed = await confirm({
      title: t('billing.creditNotes.applyTitle'),
      message: t('billing.creditNotes.applyMessage', { number }),
      confirmText: t('common.actions.apply'),
      cancelText: t('common.actions.cancel'),
      variant: 'warning',
    });
    if (!confirmed) return;

    await applyCreditNote(id);
  };

  // ✅ SKILL FACTURXPRO: Computed values pour les filtres
  const statusCounts = useMemo(() => {
    return creditNotes.reduce((acc, cn) => {
      const status = cn.status || "DRAFT";
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  }, [creditNotes]);

  // Options de filtres par statut
  const statusFilterOptions: FilterOption[] = [
    {
      value: "DRAFT",
      label: t('creditNotes.status.DRAFT'),
      icon: FileEdit,
      color: "gray",
      count: statusCounts.DRAFT || 0,
    },
    {
      value: "SENT",
      label: t('creditNotes.status.SENT'),
      icon: Send,
      color: "blue",
      count: statusCounts.SENT || 0,
    },
    {
      value: "APPLIED",
      label: t('creditNotes.status.APPLIED'),
      icon: CheckCircle,
      color: "green",
      count: statusCounts.APPLIED || 0,
    },
  ];

  // Convertir statusFilter (string) en activeStatuses (array) pour FilterBar
  const activeStatuses = useMemo(() => {
    if (statusFilter === 'ALL') return [];
    return [statusFilter];
  }, [statusFilter]);

  // Handlers pour les filtres
  const handleStatusToggle = (status: string) => {
    if (statusFilter === status) {
      setStatusFilter('ALL');
    } else {
      setStatusFilter(status as CreditNoteStatus | 'ALL');
    }
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setStatusFilter("ALL");
  };

  // ✅ Filtrer les factures disponibles pour créer un avoir (SENT ou PAID uniquement)
  const availableInvoices = useMemo(() => {
    return invoices
      .filter(inv => inv.status === 'SENT' || inv.status === 'PAID')
      .filter(inv => {
        if (!invoiceSearchTerm) return true;
        const search = invoiceSearchTerm.toLowerCase();
        return (
          inv.number?.toLowerCase().includes(search) ||
          inv.Client?.fullName?.toLowerCase().includes(search) ||
          inv.Client?.companyName?.toLowerCase().includes(search)
        );
      });
  }, [invoices, invoiceSearchTerm]);

  const handleSelectInvoice = (invoiceId: string) => {
    setShowCreateModal(false);
    navigate(`/credit-notes/new?invoiceId=${invoiceId}`);
  };

  // Helper pour obtenir l'icône selon le statut
  const getStatusIcon = (status: CreditNoteStatus) => {
    const statusMap: Record<CreditNoteStatus, any> = {
      DRAFT: FileEdit,
      SENT: Send,
      APPLIED: CheckCircle,
    };
    return statusMap[status] || FileEdit;
  };

  // Options de statut disponibles pour le Select
  const getStatusOptions = () => {
    return [
      { value: 'DRAFT', label: t('creditNotes.status.DRAFT') },
      { value: 'SENT', label: t('creditNotes.status.SENT') },
      { value: 'APPLIED', label: t('creditNotes.status.APPLIED') },
    ];
  };

  // ✅ Sprint 4 - Export CSV
  const handleExportCSV = () => {
    const columns: ExportColumn<typeof creditNotes[0]>[] = [
      { key: 'number', header: t('billing.creditNotes.tableNumber') },
      {
        key: 'Invoice.Client.companyName',
        header: t('common.client'),
        formatter: (_, row) => {
          const client = (row as any).Invoice?.Client;
          return client?.companyName || client?.fullName || '';
        },
      },
      {
        key: 'Invoice.number',
        header: t('billing.creditNotes.tableOriginInvoice'),
        formatter: (_, row) => (row as any).Invoice?.number || '',
      },
      {
        key: 'status',
        header: t('common.labels.status'),
        formatter: (value) => getStatusLabel(value as CreditNoteStatus),
      },
      {
        key: 'issueDate',
        header: t('invoices.issueDate'),
        formatter: (value) => formatDateForExport(value as string),
      },
      {
        key: 'totalHT',
        header: t('invoices.totalHT'),
        formatter: (value) => formatCurrencyForExport(value as number),
      },
      {
        key: 'totalTVA',
        header: t('templates.common.vat'),
        formatter: (value) => formatCurrencyForExport(value as number),
      },
      {
        key: 'totalTTC',
        header: t('invoices.totalTTC'),
        formatter: (value) => formatCurrencyForExport(value as number),
      },
    ];

    const today = new Date().toISOString().split('T')[0];
    downloadCSV(creditNotes, columns, {
      filename: `avoirs_export_${today}`,
      format: 'csv',
    });
  };

  return (
    <div className="px-8 pt-8 pb-12 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-4xl sm:text-5xl font-light tracking-tight text-gray-900 flex items-center gap-3">
            <FileText className="w-10 h-10 text-teal-600" />
            {t('billing.creditNotes.management')}
          </h2>
          <p className="text-base text-gray-500 font-light tracking-wide mt-2">
            {t('billing.creditNotes.totalCount', { count: creditNotes.length })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Export CSV Button */}
          {creditNotes.length > 0 && (
            <Button
              variant="outline"
              onClick={handleExportCSV}
              icon={<Download className="w-5 h-5" />}
            >
              {t('common.exportCSV')}
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() => setShowCreateModal(true)}
            icon={<Plus className="w-5 h-5" />}
          >
            {t('creditNotes.create')}
          </Button>
        </div>
      </div>

      {/* Filter Bar - Always visible when not loading */}
      {!loading && (
        <FilterBar
          searchPlaceholder={t('billing.creditNotes.searchPlaceholder')}
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          statusFilters={statusFilterOptions}
          activeStatuses={activeStatuses}
          onStatusToggle={handleStatusToggle}
          showDateFilter={false}
          onClearFilters={handleClearFilters}
          totalItems={creditNotes.length}
          filteredItems={creditNotes.length}
        />
      )}

      {/* Main Content Container */}
      <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 overflow-hidden">
        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-16">
            <Spinner size="lg" />
            <p className="mt-4 text-gray-600">{t('billing.creditNotes.loading')}</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && creditNotes.length === 0 && (
          <div className="p-8">
            <EmptyState
              icon={<FileText className="w-12 h-12" />}
              title={t('billing.creditNotes.noResults')}
              description={
                searchTerm || statusFilter !== "ALL"
                  ? t('billing.creditNotes.noResultsFilter')
                  : t('billing.creditNotes.noResultsEmpty')
              }
              ctaLabel={
                searchTerm || statusFilter !== "ALL"
                  ? t('common.clearFilters')
                  : undefined
              }
              onCtaClick={
                searchTerm || statusFilter !== "ALL"
                  ? handleClearFilters
                  : undefined
              }
            />
          </div>
        )}

        {/* Credit Notes Grid - Mobile Cards / Desktop Table */}
        {!loading && creditNotes.length > 0 && (
          <>
          {/* Mobile Card View */}
          <div className="grid grid-cols-1 gap-4 md:hidden p-6">
            {creditNotes.map((creditNote) => {
              // Dropdown menu items
              const menuItems: DropdownMenuItem[] = [
                {
                  label: t('common.actions.view'),
                  icon: <Eye className="w-4 h-4" />,
                  onClick: () => navigate(`/credit-notes/${creditNote.id}`),
                },
              ];

              if (creditNote.status === "DRAFT") {
                menuItems.push(
                  {
                    label: t('common.actions.edit'),
                    icon: <Edit className="w-4 h-4" />,
                    onClick: () =>
                      navigate(`/credit-notes/${creditNote.id}/edit`),
                  },
                  {
                    label: t('common.actions.send'),
                    icon: <Send className="w-4 h-4" />,
                    onClick: () =>
                      handleStatusChange(creditNote.id, creditNote.status),
                  },
                  {
                    label: t('common.actions.delete'),
                    icon: <Trash2 className="w-4 h-4" />,
                    variant: "danger",
                    onClick: () =>
                      handleDelete(creditNote.id, creditNote.number),
                  }
                );
              }

              if (creditNote.status === "SENT") {
                menuItems.push({
                  label: t('common.actions.apply'),
                  icon: <CheckCircle className="w-4 h-4" />,
                  onClick: () => handleApply(creditNote.id, creditNote.number),
                });
              }

              return (
                <MobileCard
                  key={creditNote.id}
                  header={
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-gray-900 mb-1">
                          {creditNote.number}
                        </h3>
                        <p className="text-sm text-gray-600 truncate">
                          {creditNote.Client?.fullName ||
                            creditNote.Client?.companyName ||
                            "-"}
                        </p>
                        {creditNote.Invoice?.number && (
                          <p className="text-xs text-blue-600 mt-1">
                            {t('billing.creditNotes.tableOriginInvoice')}: {creditNote.Invoice.number}
                          </p>
                        )}
                      </div>
                      <DropdownMenu
                        trigger={
                          <button className="p-2 hover:bg-white/30 rounded-lg transition-all">
                            <MoreVertical className="w-5 h-5 text-gray-600" />
                          </button>
                        }
                        items={menuItems}
                        align="right"
                      />
                    </div>
                  }
                  onClick={() => navigate(`/credit-notes/${creditNote.id}`)}
                  className={
                    creditNote.status === "APPLIED"
                      ? "border-green-300 bg-green-50/10"
                      : creditNote.status === "SENT"
                      ? "border-blue-300 bg-blue-50/10"
                      : ""
                  }
                >
                  {/* Date */}
                  <div className="flex items-center gap-2 text-sm mb-3">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">{t('common.labels.date')}</p>
                      <p className="font-medium text-gray-900">
                        {formatDate(creditNote.issueDate)}
                      </p>
                    </div>
                  </div>

                  {/* Reason */}
                  {creditNote.reason && (
                    <div className="text-sm mb-3">
                      <p className="text-xs text-gray-500 mb-1">{t('billing.creditNotes.tableReason')}</p>
                      <p className="text-gray-700 line-clamp-2">
                        {creditNote.reason}
                      </p>
                    </div>
                  )}

                  {/* Amount */}
                  <div className="flex items-center justify-between py-2 border-t border-gray-100 mb-3">
                    <div className="flex items-center gap-2">
                      <EuroIcon className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{t('invoices.totalTTC')}</span>
                    </div>
                    <span className="text-xl font-bold text-gray-900">
                      {formatCurrency(Number(creditNote.totalTTC))}
                    </span>
                  </div>

                  {/* Status Select */}
                  <Select
                    value={creditNote.status}
                    onChange={(e) =>
                      updateStatus(
                        creditNote.id,
                        e.target.value as CreditNoteStatus
                      )
                    }
                    size="sm"
                    variant="chip"
                    fullWidth={true}
                    icon={getStatusIcon(creditNote.status)}
                  >
                    {getStatusOptions().map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </MobileCard>
              );
            })}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableCell header>{t('billing.creditNotes.tableNumber')}</TableCell>
                  <TableCell header>{t('billing.creditNotes.tableDate')}</TableCell>
                  <TableCell header>{t('billing.creditNotes.tableOriginInvoice')}</TableCell>
                  <TableCell header>{t('billing.creditNotes.tableClient')}</TableCell>
                  <TableCell header>{t('billing.creditNotes.tableReason')}</TableCell>
                  <TableCell header align="right">
                    {t('billing.creditNotes.tableAmountHT')}
                  </TableCell>
                  <TableCell header align="right">
                    {t('billing.creditNotes.tableAmountTTC')}
                  </TableCell>
                  <TableCell header>{t('billing.creditNotes.tableStatus')}</TableCell>
                  <TableCell header align="right">
                    {t('billing.creditNotes.tableActions')}
                  </TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {creditNotes.map((creditNote) => (
                  <TableRow
                    key={creditNote.id}
                    hover
                    className={
                      creditNote.status === "APPLIED"
                        ? "bg-green-50/30"
                        : creditNote.status === "SENT"
                        ? "bg-blue-50/30"
                        : ""
                    }
                  >
                    <TableCell>
                      <span className="text-sm font-medium text-gray-900">
                        {creditNote.number}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {formatDate(creditNote.issueDate)}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm font-medium text-blue-600">
                        {creditNote.Invoice?.number || "-"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {creditNote.Client?.fullName ||
                            creditNote.Client?.companyName ||
                            "-"}
                        </div>
                        {creditNote.Client?.email && (
                          <div className="text-sm text-gray-500">
                            {creditNote.Client.email}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-xs truncate text-sm text-gray-600">
                      {creditNote.reason}
                    </TableCell>
                    <TableCell
                      align="right"
                      className="text-sm font-medium text-gray-900"
                    >
                      {formatCurrency(Number(creditNote.totalHT))}
                    </TableCell>
                    <TableCell align="right">
                      <span
                        className={`text-sm font-semibold ${
                          creditNote.status === "APPLIED"
                            ? "text-green-600"
                            : "text-gray-900"
                        }`}
                      >
                        {formatCurrency(Number(creditNote.totalTTC))}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={creditNote.status}
                        onChange={(e) =>
                          updateStatus(
                            creditNote.id,
                            e.target.value as CreditNoteStatus
                          )
                        }
                        size="sm"
                        variant="chip"
                        fullWidth={false}
                        icon={getStatusIcon(creditNote.status)}
                      >
                        {getStatusOptions().map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell align="right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() =>
                            navigate(`/credit-notes/${creditNote.id}`)
                          }
                          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                          title={t('common.actions.view')}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {creditNote.status === "DRAFT" && (
                          <>
                            <button
                              onClick={() =>
                                navigate(`/credit-notes/${creditNote.id}/edit`)
                              }
                              className="p-2 text-teal-600 hover:text-teal-900 hover:bg-teal-50 rounded-lg transition-colors"
                              title={t('common.actions.edit')}
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() =>
                                handleStatusChange(
                                  creditNote.id,
                                  creditNote.status
                                )
                              }
                              className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
                              title={t('common.actions.send')}
                            >
                              <Send className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() =>
                                handleDelete(creditNote.id, creditNote.number)
                              }
                              className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
                              title={t('common.actions.delete')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {creditNote.status === "SENT" && (
                          <button
                            onClick={() =>
                              handleApply(creditNote.id, creditNote.number)
                            }
                            className="p-2 text-green-600 hover:text-green-900 hover:bg-green-50 rounded-lg transition-colors"
                            title={t('common.actions.apply')}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-white/30">
              <div className="flex-1 flex justify-between sm:hidden">
                <Button
                  onClick={() => goToPage(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  variant="outline"
                  size="sm"
                >
                  {t('common.actions.previous')}
                </Button>
                <Button
                  onClick={() => goToPage(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  variant="outline"
                  size="sm"
                >
                  {t('common.actions.next')}
                </Button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700 font-medium">
                    {t('billing.creditNotes.paginationText', { page: pagination.page, pages: pagination.pages, total: pagination.total })}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => goToPage(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    variant="outline"
                    size="sm"
                  >
                    {t('common.actions.previous')}
                  </Button>
                  <Button
                    onClick={() => goToPage(pagination.page + 1)}
                    disabled={pagination.page === pagination.pages}
                    variant="outline"
                    size="sm"
                  >
                    {t('common.actions.next')}
                  </Button>
                </div>
              </div>
            </div>
          )}
          </>
        )}
      </div>

      {/* Create Modal - Liste de sélection de factures avec Design System v2 */}
      {showCreateModal && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowCreateModal(false)}
        >
          <div
            className="bg-white/70 backdrop-blur-xl rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-white/40"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            {/* Header V2 */}
            <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-teal-50/50 to-white">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-teal-100 rounded-2xl">
                  <FileText className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {t('billing.creditNotes.selectInvoice')}
                  </h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {t('billing.creditNotes.selectInvoiceDescription')}
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* Barre de recherche v2 */}
              <SearchInput
                value={invoiceSearchTerm}
                onChange={(e) => setInvoiceSearchTerm(e.target.value)}
                placeholder={t('billing.creditNotes.searchByNumberOrClient')}
              />

              {/* Liste des factures */}
              {loadingInvoices ? (
                <div className="flex justify-center py-8">
                  <Spinner size="md" />
                </div>
              ) : availableInvoices.length === 0 ? (
                <EmptyState
                  icon={<FileText className="w-12 h-12" />}
                  title={t('billing.creditNotes.noInvoiceAvailable')}
                  description={
                    invoiceSearchTerm
                      ? t('billing.creditNotes.noInvoiceSearch')
                      : t('billing.creditNotes.noInvoiceEmpty')
                  }
                />
              ) : (
                <div className="space-y-3">
                  {availableInvoices.map((invoice) => (
                    <button
                      key={invoice.id}
                      onClick={() => handleSelectInvoice(invoice.id)}
                      className="w-full text-left p-4 bg-white/40 border border-white/50 rounded-2xl
                        hover:border-teal-400 hover:bg-white hover:shadow-lg hover:shadow-teal-100/50
                        transition-all duration-300 group"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-bold text-gray-900 group-hover:text-teal-700 transition-colors">
                              {invoice.number}
                            </span>
                            <Badge
                              variant={
                                invoice.status === "PAID" ? "success" : "info"
                              }
                            >
                              {invoice.status === "PAID" ? t('billing.creditNotes.paidStatus') : t('billing.creditNotes.sentStatus')}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600">
                            {invoice.Client?.fullName ||
                              invoice.Client?.companyName ||
                              "-"}
                          </p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                            <span>
                              Émise le {formatDateInvoice(invoice.issueDate instanceof Date ? invoice.issueDate.toISOString() : invoice.issueDate)}
                            </span>
                            <span>•</span>
                            <span>
                              Échéance {formatDateInvoice(invoice.dueDate instanceof Date ? invoice.dueDate.toISOString() : invoice.dueDate)}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-teal-600">
                            {formatCurrencyInvoice(invoice.totalTTC)}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Footer V2 */}
            <div className="flex justify-end p-6 border-t border-gray-100 bg-gray-50/50">
              <Button
                onClick={() => setShowCreateModal(false)}
                variant="ghost"
              >
                {t('common.actions.cancel')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditNotesManagement;
